import React, { useEffect, useRef, useState } from "react";
import { translateUi } from "./i18n/index";
import { Archive, Activity, CreditCard, HardDrive, Info } from "./icons";
import {
  DashboardGradientSection,
  DASHBOARD_GLASS_TILE,
} from "./dashboardGradientPane";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

function formatBytes(bytes: number | null | undefined): string {
  const n = Number(bytes ?? 0);
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  const KB = 1024;
  const MB = KB * 1024;
  const GB = MB * 1024;
  const TB = GB * 1000;
  if (n >= TB) return `${(n / TB).toFixed(n / TB >= 10 ? 1 : 2)} TB`;
  if (n >= GB) return `${(n / GB).toFixed(n / GB >= 10 ? 1 : 2)} GB`;
  if (n >= MB) return `${(n / MB).toFixed(n / MB >= 10 ? 1 : 2)} MB`;
  if (n >= KB) return `${(n / KB).toFixed(n / KB >= 10 ? 1 : 2)} KB`;
  return `${Math.round(n)} B`;
}

const UK_VAT_RATE = 0.2;

function withVat(n: number): number {
  return Math.round(Math.max(0, n) * (1 + UK_VAT_RATE) * 100) / 100;
}

function formatGbp(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return "£0";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: v % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(v);
}

function formatCount(n: number): string {
  if (!Number.isFinite(n)) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}m`;
  if (n >= 10_000) return `${Math.round(n / 1000)}k`;
  return String(Math.round(n));
}

type CompanyUsage = {
  companyCode: string;
  companyName: string;
  databaseBytes: number;
  attachmentsBytes: number;
  totalBytes: number;
  percentUsed: number;
};

type BandwidthParty = {
  email?: string;
  companyCode: string;
  companyName?: string;
  requests: number;
  bytesIn: number;
  bytesOut: number;
};

function isUnassignedCompany(code?: string, name?: string): boolean {
  const c = String(code || "").trim().toLowerCase();
  const n = String(name || "").trim();
  return !c || c === "(none)" || c === "-" || n === "—" || n === "-";
}

function trafficPartyLabel(party: BandwidthParty): { name: string; detail: string } {
  if (isUnassignedCompany(party.companyCode, party.companyName)) {
    return {
      name: translateUi("Unassigned"),
      detail: translateUi("No company on the request — login, system admin, health checks"),
    };
  }
  const name = String(party.companyName || party.companyCode || "").trim();
  const code = String(party.companyCode || "").trim();
  return {
    name: name || code || translateUi("Unknown"),
    detail: code && name && name !== code ? code : translateUi("Tenant traffic this month"),
  };
}

function FieldHelp({ label, hint, className }: { label: string; hint: string; className?: string }) {
  return (
    <span className={cn("inline-flex max-w-full items-center gap-1", className)} title={hint}>
      <span className="truncate">{label}</span>
      <Info className="h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden />
      <span className="sr-only">{hint}</span>
    </span>
  );
}

export type SystemPerformancePayload = {
  generatedAt?: string;
  filter?: {
    companyCode?: string | null;
    companyName?: string | null;
  };
  azurePlan?: {
    region: string;
    regionSource?: "env" | "detected";
    regionAutoUpdatesOnMove?: boolean;
    currency: string;
    vatExcluded?: boolean;
    platformPreset?: { id: string; label: string } | null;
    siteName?: string | null;
    appService: {
      sku: string;
      name: string;
      tier: string;
      vcores: number;
      ramGb: number;
      diskGb?: number;
      costMonthlyGbp: number;
      source: string;
      detectedTier?: string | null;
      autoUpdatesOnScale: boolean;
      sizeAssumed?: boolean;
    };
    postgres: {
      sku: string;
      name: string;
      configured?: boolean;
      vcores: number;
      ramGb: number;
      storageGb: number;
      computeMonthlyGbp: number;
      storageMonthlyGbp: number;
      costMonthlyGbp: number;
      source: string;
      autoUpdatesOnScale: boolean;
    };
    blob: {
      sku: string;
      name: string;
      watchLimitGb: number;
      gbpPerGbMonth: number;
      estimatedMonthlyGbp: number;
      source: string;
      autoUpdatesOnScale: boolean;
    };
    bandwidth: {
      includedGb: number;
      watchLimitGb: number;
      gbpPerGbOverage: number;
      estimatedMonthlyGbp: number;
      source: string;
      autoUpdatesOnScale: boolean;
    };
    totalFixedMonthlyGbp: number;
    totalEstimatedMonthlyGbp: number;
    charges?: {
      monthToDateGbp: number;
      forecastGbp: number;
      remainingGbp: number;
      monthToDateSource: "azure" | "prorated";
      forecastSource: "azure" | "sku-list";
      periodLabel: string;
      appServiceSoFarGbp: number;
    };
    note: string;
    upgradeHint: string;
    billing?: {
      available: boolean;
      reason?: string;
      currency: string;
      monthToDateGbp: number;
      forecastGbp: number | null;
      periodLabel: string;
      topProducts: Array<{ name: string; costGbp: number }>;
      source: string;
    };
  };
  plans: {
    sqlLimitBytes: number;
    sqlLimitGb: number;
    blobLimitBytes: number;
    blobLimitGb: number;
    bandwidthLimitBytes: number;
    bandwidthLimitGb: number;
    appDiskLimitBytes?: number;
    appDiskLimitGb?: number;
    note?: string;
  };
  database: {
    kind: string;
    bytes: number;
    limitBytes: number;
    tenantEstimateBytes: number;
  };
  blob: {
    configured: boolean;
    bytes: number;
    fileCount: number;
    containers: string[];
    limitBytes: number;
    fromAccountScan: boolean;
  };
  appDisk?: {
    onAzure: boolean;
    measured: boolean;
    bytes: number;
    limitBytes: number;
    limitGb: number;
    freeBytes: number;
    sku: string;
    skuName: string;
    tier: string;
    limitSource: "live-share" | "sku" | "tier";
    folders: Array<{ id: string; label: string; path: string; bytes: number }>;
  };
  companies: CompanyUsage[];
  companyQuotaTotalBytes: number;
  bandwidth: {
    trackedSince: string | null;
    limitBytes: number;
    month: {
      requests: number;
      bytesIn: number;
      bytesOut: number;
      errors4xx: number;
      errors5xx: number;
      avgLatencyMs: number;
    };
    last24h: {
      requests: number;
      bytesIn: number;
      bytesOut: number;
      errors4xx: number;
      errors5xx: number;
      avgLatencyMs: number;
      hours: Array<{ hourUtc: string; requests: number; bytesOut: number; bytesIn: number; errors: number }>;
    };
    byUser: BandwidthParty[];
    byCompany: BandwidthParty[];
    live: { avgLatencyMs: number; p95LatencyMs: number; samples: number };
  };
};

function Meter({
  used,
  limit,
  label,
  usedLabel,
  limitLabel,
  color,
}: {
  used: number;
  limit: number;
  label: string;
  usedLabel: string;
  limitLabel: string;
  color: string;
}) {
  const pct = limit > 0 ? Math.min(100, Math.max(0, (used / limit) * 100)) : 0;
  const tone = pct >= 90 ? "bg-rose-400" : pct >= 75 ? "bg-amber-400" : color;
  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-semibold text-white">{label}</p>
        <p className="text-xs tabular-nums text-slate-300">
          {usedLabel}
          {limit > 0 ? ` / ${limitLabel}` : ""}
        </p>
      </div>
      <div className="mt-1.5 h-2.5 rounded-full bg-white/10 overflow-hidden">
        <div className={cn("h-full rounded-full transition-all", tone)} style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-1 text-[11px] text-slate-400 tabular-nums">
        {limit > 0 ? `${pct.toFixed(1)}% of Azure plan` : "No plan cap configured"}
      </p>
    </div>
  );
}

function hourBucketUtc(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  const h = String(d.getUTCHours()).padStart(2, "0");
  return `${y}-${m}-${day}T${h}:00:00Z`;
}

function formatChartHour(d: Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Europe/London",
  }).format(d);
}

function formatChartHourLong(d: Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Europe/London",
  }).format(d);
}

function niceByteCeiling(n: number): number {
  if (!Number.isFinite(n) || n <= 0) return 1024;
  const exp = 1024 ** Math.floor(Math.log(n) / Math.log(1024));
  const mant = n / exp;
  const nice = mant <= 1 ? 1 : mant <= 2 ? 2 : mant <= 5 ? 5 : 10;
  return nice * exp;
}

type TrafficHourPoint = {
  at: Date;
  bytesOut: number;
  bytesIn: number;
  requests: number;
  errors: number;
};

function last24hOutboundSeries(
  hours: Array<{ hourUtc: string; bytesOut?: number; bytesIn?: number; requests?: number; errors?: number }>
): TrafficHourPoint[] {
  const byHour = new Map<string, Omit<TrafficHourPoint, "at">>();
  for (const row of hours) {
    const raw = String(row.hourUtc || "").trim();
    if (!raw) continue;
    const parsed = new Date(raw.endsWith("Z") || raw.includes("+") ? raw : `${raw}Z`);
    if (Number.isNaN(parsed.getTime())) continue;
    const key = hourBucketUtc(parsed);
    const prev = byHour.get(key) || { bytesOut: 0, bytesIn: 0, requests: 0, errors: 0 };
    byHour.set(key, {
      bytesOut: prev.bytesOut + (Number(row.bytesOut) || 0),
      bytesIn: prev.bytesIn + (Number(row.bytesIn) || 0),
      requests: prev.requests + (Number(row.requests) || 0),
      errors: prev.errors + (Number(row.errors) || 0),
    });
  }
  const end = new Date();
  end.setUTCMinutes(0, 0, 0);
  end.setUTCSeconds(0, 0);
  return Array.from({ length: 24 }, (_, i) => {
    const at = new Date(end.getTime() - (23 - i) * 3600_000);
    const hit = byHour.get(hourBucketUtc(at));
    return {
      at,
      bytesOut: hit?.bytesOut || 0,
      bytesIn: hit?.bytesIn || 0,
      requests: hit?.requests || 0,
      errors: hit?.errors || 0,
    };
  });
}

function HourlyTrafficChart({
  hours,
}: {
  hours: Array<{ hourUtc: string; bytesOut: number; bytesIn?: number; requests?: number; errors?: number }>;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [pinnedIdx, setPinnedIdx] = useState<number | null>(null);
  const series = last24hOutboundSeries(hours);
  const rawMax = Math.max(0, ...series.map((p) => p.bytesOut));
  const yMax = niceByteCeiling(rawMax);
  const w = 420;
  const h = 168;
  const padL = 58;
  const padR = 10;
  const padT = 12;
  const padB = 28;
  const plotW = w - padL - padR;
  const plotH = h - padT - padB;
  const last = Math.max(1, series.length - 1);
  const xAt = (i: number) => padL + (i / last) * plotW;
  const yAt = (bytes: number) => padT + plotH - (bytes / yMax) * plotH;
  const yTicks = [0, yMax / 2, yMax];
  const xTicks = [0, 6, 12, 18, 23].filter((i) => i < series.length);
  const line = series
    .map((p, i) => `${i === 0 ? "M" : "L"}${xAt(i).toFixed(1)},${yAt(p.bytesOut).toFixed(1)}`)
    .join(" ");
  const area = `${line} L${xAt(series.length - 1).toFixed(1)},${(padT + plotH).toFixed(1)} L${xAt(0).toFixed(1)},${(padT + plotH).toFixed(1)} Z`;
  const peakIdx = series.reduce((best, p, i) => (p.bytesOut > series[best].bytesOut ? i : best), 0);
  const activeIdx = pinnedIdx ?? hoverIdx;
  const active = activeIdx != null ? series[activeIdx] : null;

  const tipPos = (() => {
    if (activeIdx == null || !active) return null;
    const wrap = wrapRef.current;
    const svg = svgRef.current;
    const ctm = svg?.getScreenCTM();
    if (!wrap || !svg || !ctm) {
      return { top: 8, left: (xAt(activeIdx) / w) * 100, flip: activeIdx > 14 };
    }
    const pt = svg.createSVGPoint();
    pt.x = xAt(activeIdx);
    pt.y = yAt(active.bytesOut);
    const screen = pt.matrixTransform(ctm);
    const box = wrap.getBoundingClientRect();
    const leftPct = ((screen.x - box.left) / Math.max(1, box.width)) * 100;
    const topPct = ((screen.y - box.top) / Math.max(1, box.height)) * 100;
    return { top: topPct, left: leftPct, flip: leftPct > 58 };
  })();

  return (
    <div ref={wrapRef} className="relative mt-1">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        className="h-40 w-full cursor-crosshair touch-manipulation"
        role="img"
        aria-label={translateUi("Outbound API bytes per hour for the last 24 hours. Hover or tap a point for that hour.")}
        onPointerLeave={() => setHoverIdx(null)}
      >
        {yTicks.map((tick) => {
          const y = yAt(tick);
          return (
            <g key={`y-${tick}`}>
              <line x1={padL} x2={padL + plotW} y1={y} y2={y} stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
              <text x={padL - 8} y={y + 3} textAnchor="end" fill="#94a3b8" fontSize="10" fontFamily="ui-sans-serif, system-ui">
                {formatBytes(tick)}
              </text>
            </g>
          );
        })}
        <line x1={padL} x2={padL} y1={padT} y2={padT + plotH} stroke="rgba(148,163,184,0.55)" strokeWidth="1" />
        <line x1={padL} x2={padL + plotW} y1={padT + plotH} y2={padT + plotH} stroke="rgba(148,163,184,0.55)" strokeWidth="1" />
        {xTicks.map((i) => (
          <text
            key={`x-${i}`}
            x={xAt(i)}
            y={h - 8}
            textAnchor={i === 0 ? "start" : i === 23 ? "end" : "middle"}
            fill="#94a3b8"
            fontSize="10"
            fontFamily="ui-sans-serif, system-ui"
          >
            {formatChartHour(series[i].at)}
          </text>
        ))}
        <path d={area} fill="rgba(56,189,248,0.16)" />
        <path d={line} fill="none" stroke="#38bdf8" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {series[peakIdx]?.bytesOut > 0 && activeIdx !== peakIdx ? (
          <circle cx={xAt(peakIdx)} cy={yAt(series[peakIdx].bytesOut)} r="3" fill="#e0f2fe" />
        ) : null}
        {active && activeIdx != null ? (
          <g pointerEvents="none">
            <line
              x1={xAt(activeIdx)}
              x2={xAt(activeIdx)}
              y1={padT}
              y2={padT + plotH}
              stroke="rgba(224,242,254,0.55)"
              strokeWidth="1"
              strokeDasharray="3 3"
            />
            <circle cx={xAt(activeIdx)} cy={yAt(active.bytesOut)} r="4.5" fill="#38bdf8" stroke="#e0f2fe" strokeWidth="2" />
          </g>
        ) : null}
        {series.map((_, i) => {
          const x0 = i === 0 ? padL : (xAt(i - 1) + xAt(i)) / 2;
          const x1 = i === last ? padL + plotW : (xAt(i) + xAt(i + 1)) / 2;
          return (
            <rect
              key={`hit-${i}`}
              x={x0}
              y={padT}
              width={Math.max(1, x1 - x0)}
              height={plotH}
              fill="transparent"
              onPointerEnter={() => setHoverIdx(i)}
              onPointerMove={() => setHoverIdx(i)}
              onClick={(e) => {
                e.stopPropagation();
                setPinnedIdx((prev) => (prev === i ? null : i));
              }}
            />
          );
        })}
      </svg>
      {active && activeIdx != null && tipPos ? (
        <div
          className="pointer-events-none absolute z-10 w-max min-w-[10.5rem] max-w-[14rem] rounded-lg border border-white/15 bg-slate-950/95 px-2.5 py-2 text-[11px] text-slate-200 shadow-lg backdrop-blur-sm"
          style={{
            top: `${Math.max(2, Math.min(50, tipPos.top - 28))}%`,
            left: tipPos.flip ? undefined : `${Math.min(72, tipPos.left + 1.5)}%`,
            right: tipPos.flip ? `${Math.min(72, 100 - tipPos.left + 1.5)}%` : undefined,
          }}
        >
          <p className="font-semibold text-white">{formatChartHourLong(active.at)}</p>
          <p className="mt-1 tabular-nums">
            {translateUi("Out")} <span className="font-semibold text-sky-200">{formatBytes(active.bytesOut)}</span>
          </p>
          <p className="tabular-nums">
            {translateUi("In")} <span className="text-slate-300">{formatBytes(active.bytesIn)}</span>
          </p>
          <p className="tabular-nums text-slate-400">
            {formatCount(active.requests)} {translateUi("req")}
            {active.errors > 0 ? ` · ${formatCount(active.errors)} ${translateUi("errors")}` : ""}
          </p>
        </div>
      ) : null}
    </div>
  );
}

function UsageDonut({
  used,
  limit,
  label,
  color,
}: {
  used: number;
  limit: number;
  label: string;
  color: string;
}) {
  const pct = limit > 0 ? Math.min(100, Math.max(0, (used / limit) * 100)) : used > 0 ? 100 : 0;
  const track = "rgba(255,255,255,0.12)";
  const bg =
    pct <= 0 ? `conic-gradient(${track} 0% 100%)` : `conic-gradient(${color} 0% ${pct}%, ${track} ${pct}% 100%)`;
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative h-28 w-28 rounded-full" style={{ background: bg }} role="img" aria-label={`${label} ${pct.toFixed(1)} percent`}>
        <div className="absolute inset-3 rounded-full bg-slate-950/80 flex items-center justify-center px-1">
          <span className="text-lg font-bold text-white tabular-nums leading-none">{pct.toFixed(0)}%</span>
        </div>
      </div>
      <p className="text-xs font-semibold text-slate-200">{label}</p>
      <p className="text-[11px] text-slate-400 tabular-nums text-center">
        {formatBytes(used)}
        {limit > 0 ? ` of ${formatBytes(limit)}` : ""}
      </p>
    </div>
  );
}

export function SystemAdminPerformancePanes({
  currentUserEmail,
  isMobileOrTablet = false,
  companyCode,
  companyName,
}: {
  currentUserEmail?: string;
  isMobileOrTablet?: boolean;
  companyCode?: string;
  companyName?: string;
}) {
  const [data, setData] = useState<SystemPerformancePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const scopedCode = String(companyCode || "").trim();
  const scopedLabel =
    (data?.filter?.companyName || companyName || "").trim() ||
    scopedCode ||
    "";

  useEffect(() => {
    if (!currentUserEmail) return;
    let cancelled = false;
    const headers: HeadersInit = { "X-User-Email": currentUserEmail };
    const load = async () => {
      try {
        const qs = scopedCode
          ? `?companyCode=${encodeURIComponent(scopedCode)}&_=${Date.now()}`
          : `?_=${Date.now()}`;
        const res = await fetch(`/api/system-performance${qs}`, { headers, cache: "no-store" });
        const json = (await res.json().catch(() => ({}))) as SystemPerformancePayload & { error?: string };
        if (!res.ok) throw new Error(json.error || `Failed (${res.status})`);
        if (!cancelled) {
          setData(json);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load performance.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    setLoading(true);
    void load();
    const interval = window.setInterval(() => void load(), 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [currentUserEmail, scopedCode]);

  const monthErrors =
    (data?.bandwidth.month.errors4xx || 0) + (data?.bandwidth.month.errors5xx || 0);
  const dayErrors =
    (data?.bandwidth.last24h.errors4xx || 0) + (data?.bandwidth.last24h.errors5xx || 0);
  const dayReqs = data?.bandwidth.last24h.requests || 0;
  const errorRate = dayReqs > 0 ? (dayErrors / dayReqs) * 100 : 0;

  if (loading && !data) {
    return (
      <DashboardGradientSection title={translateUi("Azure performance")} subtitle={translateUi("Loading storage and traffic…")} icon={Activity}>
        <div className="h-24 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white/70" />
        </div>
      </DashboardGradientSection>
    );
  }

  if (error && !data) {
    return (
      <DashboardGradientSection title={translateUi("Azure performance")} icon={Activity}>
        <p className="text-sm text-rose-200">{error}</p>
      </DashboardGradientSection>
    );
  }

  if (!data) return null;

  const db = data.database;
  const blob = data.blob;
  const bw = data.bandwidth;
  const monthOut = bw.month.bytesOut + bw.month.bytesIn;
  const scoped = Boolean(scopedCode);
  const scopedCompany = scoped ? data.companies[0] : null;
  const dbLabel = scoped
    ? translateUi("Tenant data (est.)")
    : db.kind === "postgres"
      ? "Azure SQL"
      : db.kind === "sqlite"
        ? "SQLite file"
        : "Database";
  const storageSubtitle = scoped
    ? `${translateUi("This company's stored data as a share of the Azure SQL and Blob plan caps.")} ${scopedLabel ? `(${scopedLabel})` : ""}`.trim()
    : translateUi("Azure SQL and Blob usage against the current plan limits. Limits follow the Azure SKUs below.");
  const bandwidthSubtitle = scoped
    ? bw.trackedSince
      ? `${translateUi("API traffic for this company since")} ${new Date(bw.trackedSince).toLocaleString("en-GB")}. ${translateUi("Share of the Azure monthly bandwidth plan.")}`
      : translateUi("Traffic for this company is recorded as requests are served. Figures appear after the first hour of use.")
    : bw.trackedSince
      ? `${translateUi("App API traffic (request + response bytes) since")} ${new Date(bw.trackedSince).toLocaleString("en-GB")}. ${translateUi("Compare outbound volume with your Azure App Service / bandwidth plan.")}`
      : translateUi("Traffic is recorded from this app as requests are served. Figures appear after the first hour of use.");

  const plan = data.azurePlan;
  const charges = plan?.charges;
  const totalSoFar = charges?.monthToDateGbp ?? 0;
  const forecastTotal = charges?.forecastGbp ?? plan?.totalEstimatedMonthlyGbp ?? 0;
  const remainingTotal = charges?.remainingGbp ?? Math.max(0, forecastTotal - totalSoFar);
  const liveBilling = charges?.monthToDateSource === "azure";
  const disk = data.appDisk;
  const diskLimitBytes = disk?.limitBytes || data.plans.appDiskLimitBytes || 0;
  const diskUsedBytes = disk?.bytes || 0;
  const diskFreeBytes = disk?.freeBytes ?? Math.max(0, diskLimitBytes - diskUsedBytes);
  const diskLimitGb = disk?.limitGb || data.plans.appDiskLimitGb || plan?.appService.diskGb || 10;
  const diskSkuLabel = disk?.skuName || plan?.appService.name || "App Service";
  const diskSkuCode = disk?.sku || plan?.appService.sku || "";
  const diskUsedPct = diskLimitBytes > 0 ? Math.min(100, (diskUsedBytes / diskLimitBytes) * 100) : 0;
  const diskRemainPct = Math.max(0, 100 - diskUsedPct);
  const diskRemainTone =
    diskUsedPct >= 90 ? "text-rose-300" : diskUsedPct >= 75 ? "text-amber-300" : "text-emerald-300";
  const diskFolderLabel = (id: string, fallback: string) => {
    const map: Record<string, string> = {
      git: translateUi("Git history (.git)"),
      node_modules: "node_modules",
      dist: translateUi("Frontend build (dist)"),
      dist_server: translateUi("API build (dist-server)"),
      src: translateUi("Source (src)"),
      public: translateUi("Public assets"),
      deployments: translateUi("Deployment history"),
      logs: translateUi("Log files"),
      npm: translateUi("npm cache"),
      other: translateUi("Other /home files"),
    };
    return map[id] || fallback;
  };
  const sourceLabel = (source: string, auto?: boolean) => {
    if (source === "unset") return translateUi("Unset");
    if (auto || source === "detected") return translateUi("Auto");
    if (source === "env") return translateUi("Env");
    if (source === "preset") return translateUi("Preset");
    return translateUi("Default");
  };

  return (
    <>
      {plan && (
        <DashboardGradientSection
          key="current-azure-plan"
          title={translateUi("Current Azure plan")}
          subtitle={
            scoped
              ? translateUi("Account SKUs and Azure billing. Company filter does not change the subscription.")
              : `${translateUi("Detected SKUs plus Azure billing when available.")} ${plan.region}${plan.regionAutoUpdatesOnMove ? ` · ${translateUi("follows this host")}` : ""} · ${translateUi("excl. VAT")}${plan.siteName ? ` · ${plan.siteName}` : ""}`
          }
          icon={CreditCard}
          collapsible
          defaultCollapsed
        >
          <div className={cn("mb-4 grid gap-3", isMobileOrTablet ? "grid-cols-1" : "grid-cols-3")}>
            {[
                {
                  label: translateUi("Total so far"),
                  value: formatGbp(totalSoFar),
                  hint: `${formatGbp(withVat(totalSoFar))} ${translateUi("incl. VAT")} · ${
                    liveBilling
                      ? translateUi("Azure invoice to date")
                      : translateUi("Accrued this month")
                  }`,
                },
                {
                  label: translateUi("Forecast"),
                  value: formatGbp(forecastTotal),
                  hint: `${formatGbp(withVat(forecastTotal))} ${translateUi("incl. VAT")} · ${translateUi("month end")}`,
                },
                {
                  label: translateUi("Remaining"),
                  value: formatGbp(remainingTotal),
                  hint: `${formatGbp(withVat(remainingTotal))} ${translateUi("incl. VAT")} · ${translateUi("left this period")}`,
                },
            ].map((tile) => (
              <div key={tile.label} className={cn(DASHBOARD_GLASS_TILE, "p-4")}>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{tile.label}</p>
                <p className="mt-1 text-xl font-bold text-white tabular-nums">{tile.value}</p>
                <p className="mt-0.5 text-[11px] text-slate-400 truncate">{tile.hint}</p>
              </div>
            ))}
          </div>

          <div className={cn("grid gap-3", isMobileOrTablet ? "grid-cols-1" : "grid-cols-2 xl:grid-cols-4")}>
            {[
              {
                label: translateUi("App Service"),
                sku: plan.appService.name,
                limit: plan.appService.vcores
                  ? `${plan.appService.vcores} vCPU · ${plan.appService.ramGb} GB RAM · ${plan.appService.diskGb ?? 10} GB ${translateUi("disk")}`
                  : plan.appService.tier,
                cost: formatGbp(plan.charges?.appServiceSoFarGbp ?? 0),
                costHint: translateUi("so far"),
                hint: `${formatGbp(plan.appService.costMonthlyGbp)} ${translateUi("forecast / mo")} · ${translateUi("billed hourly")}`,
                badge: sourceLabel(plan.appService.source, plan.appService.autoUpdatesOnScale),
              },
              {
                label: translateUi("PostgreSQL"),
                sku: plan.postgres.configured
                  ? `${plan.postgres.name} · ${plan.postgres.storageGb} GB`
                  : plan.postgres.name,
                limit: plan.postgres.configured
                  ? `${plan.postgres.vcores} vCPU · ${plan.postgres.ramGb} GB RAM`
                  : translateUi("Not showing on your Azure invoice — set AZURE_POSTGRES_SKU to track list price"),
                cost: plan.postgres.configured ? formatGbp(plan.postgres.costMonthlyGbp) : "—",
                costHint: plan.postgres.configured ? translateUi("list / mo") : "",
                hint: plan.postgres.configured
                  ? `${formatGbp(plan.postgres.computeMonthlyGbp)} compute · ${formatGbp(plan.postgres.storageMonthlyGbp)} storage`
                  : translateUi("Your billing overview currently has no PostgreSQL line this month."),
                badge: sourceLabel(plan.postgres.source),
              },
              {
                label: translateUi("Blob storage"),
                sku: plan.blob.name,
                limit: translateUi("PAYG · dashboard watch {{gb}} GB", { gb: plan.blob.watchLimitGb }),
                cost: formatGbp(plan.blob.estimatedMonthlyGbp),
                costHint: translateUi("from used bytes"),
                hint: `${formatGbp(plan.blob.gbpPerGbMonth)}/${translateUi("GB-month")} · Hot GRS`,
                badge: sourceLabel(plan.blob.source),
              },
              {
                label: translateUi("Bandwidth"),
                sku: `${plan.bandwidth.includedGb} GB ${translateUi("included")}`,
                limit:
                  plan.bandwidth.watchLimitGb > 0
                    ? `${plan.bandwidth.watchLimitGb} GB ${translateUi("watch cap")}`
                    : translateUi("No hard cap (PAYG)"),
                cost: formatGbp(plan.bandwidth.estimatedMonthlyGbp),
                costHint: translateUi("overage est."),
                hint: `${formatGbp(plan.bandwidth.gbpPerGbOverage)}/${translateUi("GB over included")}`,
                badge: sourceLabel(plan.bandwidth.source),
              },
            ].map((card) => (
              <div key={card.label} className={cn(DASHBOARD_GLASS_TILE, "p-4")}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{card.label}</p>
                  <span className="shrink-0 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-300">
                    {card.badge}
                  </span>
                </div>
                <p className="mt-1.5 text-sm font-semibold text-white leading-snug">{card.sku}</p>
                <p className="mt-0.5 text-[12px] text-slate-300">{card.limit}</p>
                <p className="mt-3 text-xl font-bold text-white tabular-nums">
                  {card.cost}
                  {card.costHint ? <span className="text-xs font-medium text-slate-400"> {card.costHint}</span> : null}
                </p>
                <p className="mt-1 text-[11px] text-slate-400 leading-snug">{card.hint}</p>
              </div>
            ))}
          </div>

          {plan.billing?.available && plan.billing.topProducts.length > 1 ? (
            <div className={cn(DASHBOARD_GLASS_TILE, "mt-4 p-4")}>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
                {translateUi("This month's products")}
              </p>
              <div className="space-y-1.5">
                {plan.billing.topProducts.map((p) => (
                  <div key={p.name} className="flex items-center justify-between gap-3 text-sm">
                    <span className="truncate text-slate-100">{p.name}</span>
                    <span className="shrink-0 tabular-nums text-slate-300">{formatGbp(p.costGbp)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className={cn(DASHBOARD_GLASS_TILE, "mt-4 p-4 flex flex-wrap items-end justify-between gap-3")}>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                {translateUi("Total cost this month")}
              </p>
              <p className="mt-1 text-2xl font-bold text-white tabular-nums">
                {formatGbp(totalSoFar)}
                <span className="ml-2 text-sm font-semibold text-slate-300">{translateUi("excl. VAT")}</span>
              </p>
              <p className="mt-1 text-xl font-bold text-emerald-200 tabular-nums">
                {formatGbp(withVat(totalSoFar))}
                <span className="ml-2 text-sm font-semibold text-emerald-200/80">{translateUi("incl. VAT (20%)")}</span>
              </p>
              <p className="mt-1.5 text-[11px] text-slate-400">
                {translateUi("Forecast")} {formatGbp(forecastTotal)} {translateUi("excl. VAT")}
                {" · "}
                {formatGbp(withVat(forecastTotal))} {translateUi("incl. VAT")}
                {" · "}
                {liveBilling
                  ? translateUi("from Azure Cost Management")
                  : translateUi("estimated to today")}
                {plan.platformPreset ? ` · ${translateUi("Preset")}: ${plan.platformPreset.label}` : ""}
              </p>
            </div>
            <p className="max-w-xl text-[11px] text-slate-500 leading-relaxed">{plan.note}</p>
          </div>
          <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">{plan.upgradeHint}</p>
        </DashboardGradientSection>
      )}

      <DashboardGradientSection
        title={translateUi("Storage vs Azure plan")}
        subtitle={storageSubtitle}
        icon={Archive}
        collapsible
        defaultCollapsed
      >
        <div className={cn("grid gap-6", isMobileOrTablet ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-[auto_1fr]")}>
          <div className="flex flex-wrap items-center justify-center gap-8">
            <UsageDonut
              used={db.bytes}
              limit={db.limitBytes}
              label={dbLabel}
              color="#818cf8"
            />
            <UsageDonut
              used={blob.bytes}
              limit={blob.limitBytes}
              label={scoped ? translateUi("Company files") : "Blob storage"}
              color="#22d3ee"
            />
          </div>
          <div className="min-w-0 space-y-4">
            <Meter
              used={db.bytes}
              limit={db.limitBytes}
              label={scoped ? translateUi("Database (this company)") : translateUi("Database")}
              usedLabel={formatBytes(db.bytes)}
              limitLabel={`${data.plans.sqlLimitGb} GB`}
              color="bg-indigo-400"
            />
            <Meter
              used={blob.bytes}
              limit={blob.limitBytes}
              label={scoped ? translateUi("Blob storage (this company)") : translateUi("Blob storage")}
              usedLabel={formatBytes(blob.bytes)}
              limitLabel={`${data.plans.blobLimitGb} GB`}
              color="bg-cyan-400"
            />
            <p className="text-[11px] text-slate-400">
              {scoped
                ? translateUi("Company snapshot against the Azure account plan. Database size is a tenant estimate.")
                : blob.fromAccountScan
                  ? `Account scan · ${blob.fileCount} blobs${blob.containers.length ? ` in ${blob.containers.join(", ")}` : ""}`
                  : blob.configured
                    ? translateUi("Blob listing still warming — showing company attachment totals.")
                    : translateUi("Azure Blob is not configured on this host.")}
              {!scoped && db.tenantEstimateBytes > 0 ? ` · Tenant CMS estimate ${formatBytes(db.tenantEstimateBytes)}` : ""}
            </p>
            {scoped && scopedCompany ? (
              <div className={cn(DASHBOARD_GLASS_TILE, "p-3")}>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
                  {translateUi("This company")}
                </p>
                <div className="space-y-1.5 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="truncate text-slate-100">
                      {scopedCompany.companyName}{" "}
                      <span className="text-slate-500 font-mono text-xs">({scopedCompany.companyCode})</span>
                    </span>
                    <span className="shrink-0 tabular-nums text-slate-300">{formatBytes(scopedCompany.totalBytes)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 text-[13px] text-slate-400">
                    <span>{translateUi("Database")}</span>
                    <span className="tabular-nums">{formatBytes(scopedCompany.databaseBytes)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 text-[13px] text-slate-400">
                    <span>{translateUi("Attachments")}</span>
                    <span className="tabular-nums">{formatBytes(scopedCompany.attachmentsBytes)}</span>
                  </div>
                </div>
              </div>
            ) : data.companies.length > 0 ? (
              <div className={cn(DASHBOARD_GLASS_TILE, "p-3")}>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
                  {translateUi("Top companies by stored files")}
                </p>
                <div className="space-y-1.5">
                  {data.companies.slice(0, 5).map((c) => (
                    <div key={c.companyCode} className="flex items-center justify-between gap-3 text-sm">
                      <span className="truncate text-slate-100">
                        {c.companyName} <span className="text-slate-500 font-mono text-xs">({c.companyCode})</span>
                      </span>
                      <span className="shrink-0 tabular-nums text-slate-300">{formatBytes(c.totalBytes)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </DashboardGradientSection>

      <DashboardGradientSection
        title={translateUi("Bandwidth & traffic")}
        subtitle={bandwidthSubtitle}
        icon={Activity}
        collapsible
        defaultCollapsed
      >
        <div className={cn("grid gap-3", isMobileOrTablet ? "grid-cols-2" : "grid-cols-2 xl:grid-cols-4")}>
          {[
            {
              label: translateUi("This month"),
              value: formatBytes(monthOut),
              hint: `${formatBytes(bw.month.bytesOut)} ${translateUi("out")} · ${formatBytes(bw.month.bytesIn)} ${translateUi("in")}`,
              help: translateUi("Outbound API bytes this calendar month. This is app traffic, not Azure Front Door."),
            },
            {
              label: translateUi("Last 24 hours"),
              value: formatBytes(bw.last24h.bytesOut + bw.last24h.bytesIn),
              hint: `${formatCount(bw.last24h.requests)} ${translateUi("requests")}`,
              help: translateUi("Request + response bytes in the last 24 hours, including all companies."),
            },
            {
              label: translateUi("Error rate (24h)"),
              value: `${errorRate.toFixed(1)}%`,
              hint: `${formatCount(dayErrors)} ${translateUi("failed of")} ${formatCount(dayReqs)} ${translateUi("requests")}`,
              help: translateUi("Percent of API calls in the last 24 hours that failed (HTTP 4xx or 5xx). 0% means every request succeeded. Lower is better."),
            },
            {
              label: translateUi("Latency"),
              value: `${bw.live.avgLatencyMs || bw.last24h.avgLatencyMs || 0} ms`,
              hint: `${translateUi("Average reply time")} · ${translateUi("p95")} ${bw.live.p95LatencyMs || "—"} ms`,
              help: translateUi("How long the API takes to answer. The large number is the average. p95 is the slower end: 95% of recent requests finished in that time or less. Lower is better."),
            },
          ].map((stat) => (
            <div key={stat.label} className={cn(DASHBOARD_GLASS_TILE, "p-4")} title={stat.help}>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                <FieldHelp label={stat.label} hint={stat.help} />
              </p>
              <p className="mt-1 text-xl font-bold text-white tabular-nums">{stat.value}</p>
              <p className="mt-0.5 text-[11px] text-slate-400">{stat.hint}</p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500">{stat.help}</p>
            </div>
          ))}
        </div>

        <div className="mt-4">
          <Meter
            used={monthOut}
            limit={bw.limitBytes}
            label={translateUi("Monthly bandwidth")}
            usedLabel={formatBytes(monthOut)}
            limitLabel={bw.limitBytes > 0 ? `${data.plans.bandwidthLimitGb} GB` : translateUi("uncapped")}
            color="bg-sky-400"
          />
          <p className="mt-1 text-[11px] text-slate-500">
            {translateUi("Compared with the Azure plan outbound allowance. Uncapped means no billed overage line is configured.")}
          </p>
        </div>

        <div className={cn("mt-4 grid gap-4", isMobileOrTablet ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2")}>
          <div className={cn(DASHBOARD_GLASS_TILE, "p-4")}>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              <FieldHelp
                label={translateUi("Outbound bytes · last 24 hours")}
                hint={translateUi("Hourly outbound API bytes. Gaps mean no traffic in that hour.")}
              />
            </p>
            <p className="mt-0.5 text-[11px] text-slate-500">
              {translateUi("Y axis is outbound bytes per hour. X axis is the last 24 hours in UK time. Hover or tap a point for that hour's out, in, and requests.")}
            </p>
            <HourlyTrafficChart hours={bw.last24h.hours || []} />
          </div>
          <div className={cn(DASHBOARD_GLASS_TILE, "p-4")}>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              <FieldHelp
                label={scoped ? translateUi("Heaviest users in this company") : translateUi("Heaviest users this month")}
                hint={translateUi("Signed-in accounts that generated the most API traffic (in + out) this month.")}
              />
            </p>
            <p className="mt-0.5 mb-2 text-[11px] text-slate-500">
              {translateUi("Size is total request + response bytes. Line under the email is company and request count.")}
            </p>
            {bw.byUser.length === 0 ? (
              <p className="text-sm text-slate-400">{translateUi("No traffic recorded yet.")}</p>
            ) : (
              <div className="space-y-1.5">
                {bw.byUser.slice(0, 6).map((u) => {
                  const party = trafficPartyLabel(u);
                  return (
                    <div key={`${u.email}-${u.companyCode}`} className="flex items-center justify-between gap-3 text-sm">
                      <span className="min-w-0 truncate text-slate-100">
                        {u.email}
                        <span className="block text-[11px] text-slate-500 truncate">
                          {party.name} · {formatCount(u.requests)} {translateUi("req")}
                        </span>
                      </span>
                      <span
                        className="shrink-0 tabular-nums text-slate-300"
                        title={`${formatBytes(u.bytesOut)} ${translateUi("out")} · ${formatBytes(u.bytesIn)} ${translateUi("in")}`}
                      >
                        {formatBytes(u.bytesOut + u.bytesIn)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {!scoped && bw.byCompany.length > 0 && (
          <div className={cn(DASHBOARD_GLASS_TILE, "p-4 mt-4")}>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              <FieldHelp
                label={translateUi("By company this month")}
                hint={translateUi("API traffic grouped by the company on each request. Unassigned has no company header.")}
              />
            </p>
            <p className="mt-0.5 mb-3 text-[11px] leading-relaxed text-slate-500">
              {translateUi("Each row is one tenant. Traffic is request + response bytes this calendar month. Unassigned is login, system admin, and other calls with no company.")}
            </p>
            <div className="mb-1.5 hidden sm:grid grid-cols-[minmax(0,1fr)_auto_auto] gap-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              <span>{translateUi("Company")}</span>
              <span className="text-right">{translateUi("Requests")}</span>
              <span className="text-right">{translateUi("Traffic")}</span>
            </div>
            <div className="space-y-2">
              {bw.byCompany.slice(0, 8).map((c) => {
                const party = trafficPartyLabel(c);
                const total = formatBytes(c.bytesOut + c.bytesIn);
                return (
                  <div
                    key={c.companyCode || party.name}
                    className="grid grid-cols-1 gap-0.5 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-center sm:gap-3 text-sm"
                    title={`${party.detail} · ${formatBytes(c.bytesOut)} ${translateUi("out")} · ${formatBytes(c.bytesIn)} ${translateUi("in")}`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-slate-100">{party.name}</span>
                      <span className="block truncate text-[11px] text-slate-500">{party.detail}</span>
                    </span>
                    <span className="tabular-nums text-slate-400 sm:text-right">
                      {formatCount(c.requests)}
                      <span className="sm:hidden"> {translateUi("req")}</span>
                    </span>
                    <span className="tabular-nums text-slate-200 sm:text-right">{total}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <p className="mt-3 text-[11px] text-slate-500">
          {translateUi("Bandwidth is measured at the app (API in/out), not Azure Front Door. Month errors:")}{" "}
          {formatCount(monthErrors)}. {data.plans.note}
        </p>
      </DashboardGradientSection>

      <DashboardGradientSection
        title={translateUi("App Service disk vs plan")}
        subtitle={
          scoped
            ? translateUi("Persistent /home disk for this App Service plan. Company filter does not change the host quota.")
            : translateUi("How full the App Service file system is against this plan's disk quota. The cap follows the SKU (10 GB on Basic, 50 GB on Standard, 250 GB on Premium) after you scale and the app restarts.")
        }
        icon={HardDrive}
        collapsible
        defaultCollapsed
      >
        <div className={cn("grid gap-6", isMobileOrTablet ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-[auto_1fr]")}>
          <div className="flex flex-wrap items-center justify-center gap-8">
            <UsageDonut
              used={diskUsedBytes}
              limit={diskLimitBytes}
              label={translateUi("Plan disk")}
              color={diskUsedPct >= 90 ? "#fb7185" : diskUsedPct >= 75 ? "#fbbf24" : "#34d399"}
            />
          </div>
          <div className="min-w-0 space-y-4">
            <div className={cn("grid gap-3", isMobileOrTablet ? "grid-cols-1" : "grid-cols-3")}>
              {[
                {
                  label: translateUi("Used"),
                  value: formatBytes(diskUsedBytes),
                  hint: disk?.measured
                    ? translateUi("Live /home share")
                    : translateUi("Measured when this app is running on Azure"),
                  emphasize: false,
                },
                {
                  label: translateUi("Plan cap"),
                  value: `${diskLimitGb} GB`,
                  hint: `${diskSkuLabel}${diskSkuCode ? ` · ${diskSkuCode}` : ""}`,
                  emphasize: false,
                },
                {
                  label: translateUi("Remaining"),
                  value: formatBytes(diskFreeBytes),
                  hint: `${diskRemainPct.toFixed(0)}% ${translateUi("free")}`,
                  emphasize: true,
                },
              ].map((tile) => (
                <div key={tile.label} className={cn(DASHBOARD_GLASS_TILE, "p-4")}>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{tile.label}</p>
                  <p className={cn("mt-1 text-xl font-bold tabular-nums text-white", tile.emphasize ? diskRemainTone : "")}>
                    {tile.value}
                  </p>
                  <p className="mt-0.5 text-[11px] text-slate-400 truncate">{tile.hint}</p>
                </div>
              ))}
            </div>
            <Meter
              used={diskUsedBytes}
              limit={diskLimitBytes}
              label={translateUi("App Service file system")}
              usedLabel={formatBytes(diskUsedBytes)}
              limitLabel={`${diskLimitGb} GB`}
              color="bg-emerald-400"
            />
            <p className="text-[11px] text-slate-400 leading-relaxed">
              {disk?.onAzure
                ? disk.limitSource === "live-share"
                  ? translateUi("Quota is the live Azure /home share for this plan. Scaling Basic → Standard → Premium updates the cap after restart (10 GB / 50 GB / 250 GB).")
                  : translateUi("Quota is taken from the detected App Service SKU. Scale the plan and restart to refresh.")
                : translateUi("This host is not Azure App Service, so used bytes are 0. The cap still follows the catalog SKU (Basic B1 = 10 GB).")}
            </p>
          </div>
        </div>
        {disk?.folders && disk.folders.length > 0 ? (
          <div className={cn(DASHBOARD_GLASS_TILE, "mt-4 p-3")}>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
              {translateUi("What's using the disk")}
            </p>
            <div className="space-y-1.5">
              {disk.folders.map((f) => (
                <div key={f.id} className="flex items-center justify-between gap-3 text-sm">
                  <span className="truncate text-slate-100">{diskFolderLabel(f.id, f.label)}</span>
                  <span className="shrink-0 tabular-nums text-slate-300">{formatBytes(f.bytes)}</span>
                </div>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-slate-500 leading-relaxed">
              {translateUi("dist is the live website (~tens of MB). A large .git folder means the deploy zip included Git history — that is not needed at runtime.")}
            </p>
          </div>
        ) : null}
      </DashboardGradientSection>
    </>
  );
}

export default SystemAdminPerformancePanes;
